import {
  appUpdateCoordinator,
  type AppUpdateCoordinator,
} from './app-update-coordinator';

const CHUNK_RELOAD_PREFIX = 'gn:chunk-reload:';

declare const __APP_BUILD_ID__: string;

function errorText(error: unknown): string {
  if (error instanceof Error) return `${error.name} ${error.message}`;
  return typeof error === 'string' ? error : '';
}

export function isDynamicChunkError(error: unknown): boolean {
  return /(?:chunkloaderror|loading (?:css )?chunk|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed)/i.test(
    errorText(error),
  );
}

export function attemptDynamicChunkRecovery(
  error: unknown,
  reload: () => void = () => window.location.reload(),
  coordinator: AppUpdateCoordinator = appUpdateCoordinator,
): boolean {
  if (!isDynamicChunkError(error) || typeof window === 'undefined') return false;

  const buildId = typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : 'development';
  const key = `${CHUNK_RELOAD_PREFIX}${buildId}`;

  try {
    if (window.sessionStorage.getItem(key) === '1') return false;
    window.sessionStorage.setItem(key, '1');
  } catch {
    return false;
  }

  const recovery = coordinator.hasWaitingUpdate()
    ? coordinator.activate()
    : coordinator.activate(() => {
      coordinator.reloadOnce(reload);
    });
  void recovery.then((result) => {
    if (result.status === 'activating') return;
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      return;
    }
  });
  return true;
}

export function createIncidentId(): string {
  const value = globalThis.crypto?.randomUUID?.()
    ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return value.slice(0, 36);
}
